import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown, faChevronRight, faSearch, faSync, faServer,
  faBan, faCheckSquare, faSquare, faExclamationTriangle,
  faTimes, faCheck, faSpinner, faPlay, faBolt, faFilter,
  faTrash, faBell, faBellSlash, faInfoCircle, faTerminal, faExternalLinkAlt,
  faFileExcel
} from '@fortawesome/free-solid-svg-icons';
import { faAws } from '@fortawesome/free-brands-svg-icons';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const AWS_REGION_NAMES: Record<string, string> = {
  'us-east-1': 'N. Virginia', 'us-east-2': 'Ohio',
  'us-west-1': 'N. California', 'us-west-2': 'Oregon',
  'ap-south-1': 'Mumbai', 'ap-south-2': 'Hyderabad',
  'ap-northeast-1': 'Tokyo', 'ap-northeast-2': 'Seoul', 'ap-northeast-3': 'Osaka',
  'ap-southeast-1': 'Singapore', 'ap-southeast-2': 'Sydney',
  'ap-southeast-3': 'Jakarta', 'ap-southeast-4': 'Melbourne',
  'ap-east-1': 'Hong Kong',
  'eu-central-1': 'Frankfurt', 'eu-central-2': 'Zurich',
  'eu-west-1': 'Ireland', 'eu-west-2': 'London', 'eu-west-3': 'Paris',
  'eu-north-1': 'Stockholm', 'eu-south-1': 'Milan', 'eu-south-2': 'Spain',
  'ca-central-1': 'Canada', 'ca-west-1': 'Calgary',
  'sa-east-1': 'São Paulo',
  'me-south-1': 'Bahrain', 'me-central-1': 'UAE',
  'af-south-1': 'Cape Town',
  'il-central-1': 'Tel Aviv'
};

// ────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────

const formatAge = (ageInDays?: number): string => {
  if (!ageInDays || ageInDays <= 0) return '-';
  if (ageInDays < 30) return `${ageInDays} Days`;
  const months = Math.floor(ageInDays / 30);
  const days = ageInDays % 30;
  if (months < 12) return days > 0 ? `${months} Months and ${days} Days` : `${months} Months`;
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  return remMonths > 0 ? `${years} Years and ${remMonths} Months` : `${years} Years`;
};

const formatSavings = (amount: number): string => {
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
  return `$${amount.toFixed(2)}`;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
};

// Service icon color mapping
const SERVICE_COLORS: Record<string, string> = {
  AMI: 'bg-yellow-500',
  EC2: 'bg-orange-500',
  EBS_VOLUME: 'bg-purple-500',
  ELASTIC_IP: 'bg-gray-400',
  EBS_SNAPSHOT: 'bg-green-500',
  NAT_GATEWAY: 'bg-orange-400',
  ELB: 'bg-purple-400',
  VPC_ENDPOINT: 'bg-pink-500',
  RDS: 'bg-blue-500',
  EKS: 'bg-blue-400',
  EC2_RIGHTSIZING: 'bg-orange-500',
  STORAGE_OPT: 'bg-purple-500',
  SAVINGS_PLAN: 'bg-green-600',
  RESERVED_INSTANCES: 'bg-blue-600',
  NETWORK_OPT: 'bg-teal-500',
  RDS_OPT: 'bg-blue-500',
  GENERAL_OPT: 'bg-indigo-500',
  CLOUDFRONT: 'bg-purple-600',
};

const SERVICE_ABBR: Record<string, string> = {
  AMI: 'AMI', EC2: 'EC2', EBS_VOLUME: 'EBS', ELASTIC_IP: 'EIP',
  EBS_SNAPSHOT: 'SNAP', NAT_GATEWAY: 'NAT', ELB: 'ELB',
  VPC_ENDPOINT: 'VPC', RDS: 'RDS', EKS: 'EKS',
  EC2_RIGHTSIZING: 'EC2', STORAGE_OPT: 'STR', SAVINGS_PLAN: 'SP',
  RESERVED_INSTANCES: 'RI', NETWORK_OPT: 'NET', RDS_OPT: 'RDS',
  GENERAL_OPT: 'OPT',
  CLOUDFRONT: 'CLO',
};

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

interface Finding {
  accountId: string;
  resourceId: string;
  resourceName: string;
  resourceType: string;
  region: string;
  potentialSavings: number;
  ageInDays?: number;
  description: string;
  action: string;
  awsCommands?: string[];
  automated: boolean;
  severity: string;
  snoozed?: boolean;
  consoleUrl?: string;
  cnames?: string[];
}

interface ServiceGroup {
  serviceId: string;
  serviceName: string;
  totalSavings: number;
  count: number;
  snoozedCount: number;
  autoRemediateAvailable: boolean;
  findings: Finding[];
}

interface Category {
  id: string;
  label: string;
  totalSavings: number;
  services: ServiceGroup[];
}

interface BreakdownData {
  categories: Category[];
  summary: {
    totalFindings: number;
    totalSavings: number;
    lastScanAt: string | null;
    requiresScan?: boolean;
    snoozedCount: number;
  };
}

// ────────────────────────────────────────────────────────
// Confirm Modal
// ────────────────────────────────────────────────────────

interface ConfirmModalProps {
  findings: Finding[];
  onConfirm: () => void;
  onCancel: () => void;
  isBulk: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({ findings, onConfirm, onCancel, isBulk }) => {
  const totalSavings = findings.reduce((s, f) => s + f.potentialSavings, 0);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg mx-4">
        <div className="bg-red-600 rounded-t-lg px-5 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2 text-white">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="font-semibold text-sm">Confirm Auto Remediation</span>
          </div>
          <button onClick={onCancel} className="text-white hover:text-red-200">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="p-5">
          <p className="text-sm text-gray-700 mb-3">
            You are about to <strong>permanently delete</strong> the following {findings.length} resource{findings.length > 1 ? 's' : ''}. This action <strong>cannot be undone</strong>.
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded max-h-48 overflow-y-auto mb-4">
            {findings.slice(0, 20).map((f, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 border-b border-gray-100 last:border-0">
                <div>
                  <div className="text-xs font-mono text-gray-800">{f.resourceName || f.resourceId}</div>
                  <div className="text-xs text-gray-500">{f.region} · {f.resourceType}</div>
                </div>
                <div className="text-xs font-semibold text-green-600">${f.potentialSavings.toFixed(2)}/mo</div>
              </div>
            ))}
            {findings.length > 20 && (
              <div className="px-3 py-2 text-xs text-gray-500 text-center">...and {findings.length - 20} more</div>
            )}
          </div>
          <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded px-3 py-2 mb-4">
            <span className="text-xs text-green-800 font-medium">Potential Monthly Savings</span>
            <span className="text-sm font-bold text-green-700">${totalSavings.toFixed(2)}</span>
          </div>
          <div className="flex space-x-3">
            <button onClick={onCancel} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={onConfirm} className="flex-1 px-4 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 font-medium">
              <FontAwesomeIcon icon={faTrash} className="mr-2" />
              Confirm Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────
// Snooze Modal
// ────────────────────────────────────────────────────────

interface SnoozeModalProps {
  findings: Finding[];
  onConfirm: (days: number, reason: string) => void;
  onCancel: () => void;
}

const SnoozeModal: React.FC<SnoozeModalProps> = ({ findings, onConfirm, onCancel }) => {
  const [days, setDays] = useState(30);
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md mx-4">
        <div className="bg-yellow-500 rounded-t-lg px-5 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2 text-white">
            <FontAwesomeIcon icon={faBell} />
            <span className="font-semibold text-sm">Snooze Recommendation{findings.length > 1 ? 's' : ''}</span>
          </div>
          <button onClick={onCancel} className="text-white hover:text-yellow-200">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="p-5">
          <p className="text-xs text-gray-600 mb-4">
            Hide {findings.length} recommendation{findings.length > 1 ? 's' : ''} for a period of time. They will reappear after the snooze period ends.
          </p>
          <div className="mb-3">
            <label className="text-xs font-medium text-gray-700 mb-1 block">Snooze for</label>
            <div className="flex space-x-2">
              {[7, 30, 90].map(d => (
                <button key={d} onClick={() => setDays(d)}
                  className={`flex-1 py-2 rounded text-xs font-medium border transition-colors ${days === d ? 'bg-yellow-500 text-white border-yellow-500' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  {d} Days
                </button>
              ))}
            </div>
          </div>
          <div className="mb-4">
            <label className="text-xs font-medium text-gray-700 mb-1 block">Reason (optional)</label>
            <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Will review next sprint"
              className="w-full border border-gray-300 rounded px-3 py-2 text-xs focus:outline-none focus:border-yellow-400" />
          </div>
          <div className="flex space-x-3">
            <button onClick={onCancel} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">Cancel</button>
            <button onClick={() => onConfirm(days, reason)} className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded text-sm hover:bg-yellow-600 font-medium">
              <FontAwesomeIcon icon={faBell} className="mr-2" />
              Snooze
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────
// Service Row (accordion)
// ────────────────────────────────────────────────────────

interface ServiceRowProps {
  service: ServiceGroup;
  isExpanded: boolean;
  onToggle: () => void;
  regionFilter: string;
  accountFilter: string;
  showSnoozed: boolean;
  onRemediateOne: (finding: Finding) => void;
  onRemediateBulk: (findings: Finding[]) => void;
  onSnoozeOne: (finding: Finding) => void;
  onSnoozeBulk: (findings: Finding[]) => void;
  remediatingIds: Set<string>;
  accountNameMap: Record<string, string>;
}

const ServiceRow: React.FC<ServiceRowProps> = ({
  service, isExpanded, onToggle, regionFilter, accountFilter, showSnoozed,
  onRemediateOne, onRemediateBulk, onSnoozeOne, onSnoozeBulk,
  remediatingIds, accountNameMap
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const visibleFindings = service.findings.filter(f => {
    if (!showSnoozed && f.snoozed) return false;
    if (regionFilter !== 'All Regions' && f.region !== regionFilter) return false;
    if (accountFilter !== 'All Accounts' && f.accountId !== accountFilter && f.accountId !== 'N/A') return false;
    if (search) {
      const q = search.toLowerCase();
      return f.resourceId.toLowerCase().includes(q) ||
        (f.resourceName || '').toLowerCase().includes(q) ||
        f.region.toLowerCase().includes(q) ||
        f.accountId.toLowerCase().includes(q);
    }
    return true;
  });

  const activeFindings = visibleFindings.filter(f => !f.snoozed);
  const allSelected = activeFindings.length > 0 && activeFindings.every(f => selected.has(f.resourceId));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(activeFindings.map(f => f.resourceId)));
    }
  };

  const toggleOne = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectedFindings = activeFindings.filter(f => selected.has(f.resourceId));

  const colorClass = SERVICE_COLORS[service.serviceId] || 'bg-gray-400';
  const abbr = SERVICE_ABBR[service.serviceId] || service.serviceId.slice(0, 3);

  const filteredCount = visibleFindings.length;
  const filteredSavings = visibleFindings.reduce((sum, f) => sum + (f.potentialSavings || 0), 0);

  // Hide service row entirely when no findings match current filters
  if (filteredCount === 0) return null;

  return (
    <div className="border-b border-gray-100 last:border-0">
      {/* Service header row */}
      <div
        className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer select-none"
        onClick={onToggle}
      >
        <FontAwesomeIcon
          icon={isExpanded ? faChevronDown : faChevronRight}
          className="text-gray-400 text-xs w-4 mr-3"
        />
        <div className={`w-8 h-8 rounded flex items-center justify-center mr-3 flex-shrink-0 ${colorClass}`}>
          <span className="text-white text-xs font-bold">{abbr}</span>
        </div>
        <div className="flex-1 text-sm font-medium text-gray-800">{service.serviceName}</div>
        <div className="flex items-center space-x-8 mr-4">
          {filteredSavings > 0 && (
            <div className="text-center">
              <div className="text-xs text-gray-500">Potential Savings</div>
              <div className="text-sm font-semibold text-gray-900">{formatSavings(filteredSavings)}</div>
            </div>
          )}
          <div className="text-center">
            <div className="text-xs text-gray-500">Recommendations</div>
            <div className="text-sm font-semibold text-gray-900">{filteredCount}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500">Auto Remediate</div>
            {service.autoRemediateAvailable ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                Available
              </span>
            ) : (
              <span className="text-sm text-gray-400">-</span>
            )}
          </div>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-gray-100 bg-white">
          {/* Toolbar */}
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search"
                  className="pl-7 pr-3 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-400 w-48"
                  onClick={e => e.stopPropagation()}
                />
              </div>
              <label className="flex items-center space-x-1.5 cursor-pointer" onClick={e => e.stopPropagation()}>
                <div className={`w-8 h-4 rounded-full transition-colors ${showSnoozed ? 'bg-blue-500' : 'bg-gray-300'}`}
                  style={{ position: 'relative' }}>
                  <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${showSnoozed ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </div>
                <span className="text-xs text-gray-600">Show snoozed</span>
              </label>
            </div>
            <div className="flex items-center space-x-2" onClick={e => e.stopPropagation()}>
              {selected.size > 0 && (
                <>
                  <span className="text-xs text-gray-500">{selected.size} selected</span>
                  <button
                    onClick={() => onSnoozeBulk(selectedFindings)}
                    className="px-3 py-1.5 text-xs border border-yellow-400 text-yellow-700 rounded hover:bg-yellow-50 flex items-center space-x-1"
                  >
                    <FontAwesomeIcon icon={faBell} className="text-xs" />
                    <span>Snooze</span>
                  </button>
                  {service.autoRemediateAvailable && (
                    <button
                      onClick={() => onRemediateBulk(selectedFindings)}
                      className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 flex items-center space-x-1"
                    >
                      <FontAwesomeIcon icon={faBolt} className="text-xs" />
                      <span>Auto Remediate ({selected.size})</span>
                    </button>
                  )}
                </>
              )}
              {selected.size === 0 && service.autoRemediateAvailable && (
                <button
                  onClick={() => onRemediateBulk(activeFindings)}
                  className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 flex items-center space-x-1"
                >
                  <FontAwesomeIcon icon={faBolt} className="text-xs" />
                  <span>Auto Remediate All</span>
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-blue-600 text-white">
                  <th className="w-8 px-3 py-2">
                    <button onClick={e => { e.stopPropagation(); toggleAll(); }}>
                      <FontAwesomeIcon icon={allSelected ? faCheckSquare : faSquare} className="text-white" />
                    </button>
                  </th>
                  <th className="text-left px-3 py-2 font-medium">Account Id</th>
                  <th className="text-left px-3 py-2 font-medium">Resource</th>
                  {service.serviceId === 'CLOUDFRONT' && <th className="text-left px-3 py-2 font-medium">CNAME</th>}
                  {service.serviceId === 'ECR_REPO' && <th className="text-right px-3 py-2 font-medium">Image Count</th>}
                  {service.serviceId === 'ASG' && <th className="text-left px-3 py-2 font-medium">Version ID</th>}
                  {service.serviceId === 'S3_BUCKET' && <th className="text-right px-3 py-2 font-medium">Size in GB</th>}
                  <th className="text-left px-3 py-2 font-medium">Region</th>
                  {service.serviceId !== 'ECR_REPO' && service.serviceId !== 'ASG' && service.serviceId !== 'S3_BUCKET' && (
                    <th className="text-right px-3 py-2 font-medium">Potential Savings</th>
                  )}
                  <th className="text-left px-3 py-2 font-medium">Recommendation Age</th>
                  <th className="text-left px-3 py-2 font-medium w-64">Description</th>
                  <th className="text-left px-3 py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleFindings.length === 0 && (
                  <tr>
                    <td colSpan={service.serviceId === 'CLOUDFRONT' ? 9 : 8} className="px-4 py-6 text-center text-gray-400">
                      {search ? 'No results found' : 'No recommendations'}
                    </td>
                  </tr>
                )}
                {visibleFindings.map((finding, idx) => {
                  const isSelected = selected.has(finding.resourceId);
                  const isRemediating = remediatingIds.has(finding.resourceId);
                  const descriptionBullets = finding.description.split('\n').filter(Boolean);

                  return (
                    <tr key={finding.resourceId}
                      className={`border-b border-gray-100 transition-colors ${finding.snoozed ? 'opacity-40' : 'hover:bg-gray-50'} ${isSelected ? 'bg-blue-50' : ''}`}>
                      <td className="px-3 py-2">
                        {!finding.snoozed && (
                          <button onClick={() => toggleOne(finding.resourceId)}>
                            <FontAwesomeIcon icon={isSelected ? faCheckSquare : faSquare} className={isSelected ? 'text-blue-600' : 'text-gray-300'} />
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700" title={finding.accountId}>
                        {accountNameMap[finding.accountId] || finding.accountId}
                      </td>
                      <td className="px-3 py-2 font-mono text-gray-800 max-w-xs">
                        <div className="truncate" title={finding.resourceName || finding.resourceId}>
                          {finding.resourceName || finding.resourceId}
                        </div>
                        {finding.resourceName && finding.resourceName !== finding.resourceId && (
                          <div className="text-gray-400 truncate text-xs" title={finding.resourceId}>
                            {finding.resourceId}
                          </div>
                        )}
                      </td>
                      {service.serviceId === 'CLOUDFRONT' && (
                        <td className="px-3 py-2 text-gray-700 max-w-xs">
                          {finding.cnames && finding.cnames.length > 0 ? (
                            <div className="space-y-0.5">
                              {finding.cnames.map((c, i) => (
                                <div key={i} className="truncate font-mono text-xs" title={c}>{c}</div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      )}
                      {service.serviceId === 'ECR_REPO' && (
                        <td className="px-3 py-2 text-right text-gray-700">{(finding as any).imageCount ?? '-'}</td>
                      )}
                      {service.serviceId === 'ASG' && (
                        <td className="px-3 py-2 text-gray-500 text-xs font-mono">{(finding as any).versionId || '-'}</td>
                      )}
                      {service.serviceId === 'S3_BUCKET' && (
                        <td className="px-3 py-2 text-right text-gray-700">
                          {(finding as any).sizeGB > 0 ? (finding as any).sizeGB : '-'}
                        </td>
                      )}
                      <td className="px-3 py-2 text-gray-700">{finding.region}</td>
                      {service.serviceId !== 'ECR_REPO' && service.serviceId !== 'ASG' && service.serviceId !== 'S3_BUCKET' && (
                        <td className="px-3 py-2 text-right font-semibold text-gray-900">
                          ${finding.potentialSavings.toFixed(2)}
                        </td>
                      )}
                      <td className="px-3 py-2 text-gray-600">{formatAge(finding.ageInDays)}</td>
                      <td className="px-3 py-2 text-gray-700 w-64">
                        <ul className="list-disc list-inside space-y-0.5">
                          {descriptionBullets.length > 1
                            ? descriptionBullets.map((b, i) => <li key={i}>{b}</li>)
                            : <li>{finding.description}</li>
                          }
                        </ul>
                      </td>
                      <td className="px-3 py-2">
                        {finding.snoozed ? (
                          <span className="inline-flex items-center space-x-1 text-yellow-600">
                            <FontAwesomeIcon icon={faBell} className="text-xs" />
                            <span>Snoozed</span>
                          </span>
                        ) : (
                          <div className="flex items-center space-x-1.5">
                            {service.autoRemediateAvailable && (
                              <button
                                onClick={() => onRemediateOne(finding)}
                                disabled={isRemediating}
                                className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center space-x-1 whitespace-nowrap"
                              >
                                {isRemediating ? (
                                  <FontAwesomeIcon icon={faSpinner} className="text-xs animate-spin" />
                                ) : (
                                  <FontAwesomeIcon icon={faPlay} className="text-xs" />
                                )}
                                <span>{finding.action}</span>
                              </button>
                            )}
                            {!service.autoRemediateAvailable && (
                              <span className="text-gray-600 text-xs">{finding.action}</span>
                            )}
                            {finding.consoleUrl && (
                              <a
                                href={finding.consoleUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-1.5 py-1 border border-blue-300 text-blue-600 rounded hover:bg-blue-50 flex items-center space-x-1 whitespace-nowrap"
                                title="View in AWS Console"
                                onClick={e => e.stopPropagation()}
                              >
                                <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
                                <span>Console</span>
                              </a>
                            )}
                            <button
                              onClick={() => onSnoozeOne(finding)}
                              className="px-1.5 py-1 border border-gray-300 text-gray-500 rounded hover:bg-gray-50"
                              title="Snooze"
                            >
                              <FontAwesomeIcon icon={faBell} className="text-xs" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────
// Main Panel
// ────────────────────────────────────────────────────────

interface RecommendationsPanelProps {
  onDeployRecommendation?: (recommendationId: string) => void;
  loading?: boolean;
  costData?: any;
  externalScanTrigger?: number;
  onScanComplete?: () => void;
  onScanProgress?: (message: string) => void;
  onBreakdownLoad?: (totalSavings: number, totalFindings: number) => void;
}

export const RecommendationsPanel: React.FC<RecommendationsPanelProps> = ({ externalScanTrigger, onScanComplete, onScanProgress, onBreakdownLoad }) => {
  const [breakdown, setBreakdown] = useState<BreakdownData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [expandedService, setExpandedService] = useState<string | null>(null);
  const [regionFilter, setRegionFilter] = useState('All Regions');
  const [accountFilter, setAccountFilter] = useState('All Accounts');
  const [showSnoozed, setShowSnoozed] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState<{accountId: string; name: string}[]>([]);
  const [remediatingIds, setRemediatingIds] = useState<Set<string>>(new Set());
  const [remediationResults, setRemediationResults] = useState<any[]>([]);
  const [showResultsModal, setShowResultsModal] = useState(false);

  // Confirm modal state
  const [confirmFindings, setConfirmFindings] = useState<Finding[] | null>(null);

  // Snooze modal state
  const [snoozeFindings, setSnoozeFindings] = useState<Finding[] | null>(null);

  const fetchBreakdown = useCallback(async () => {
    setIsLoading(true);
    console.log('📊 [FE-BREAKDOWN] Fetching breakdown...');
    try {
      const res = await fetch(`${API_BASE}/api/cost/recommendations/breakdown`, { headers: getAuthHeaders() });
      console.log('📊 [FE-BREAKDOWN] HTTP status:', res.status);
      const data = await res.json();
      console.log('📊 [FE-BREAKDOWN] Response:', JSON.stringify(data).slice(0, 500));
      if (data.success) {
        setBreakdown(data.data);
        console.log('📊 [FE-BREAKDOWN] Categories:', data.data.categories.map((c: any) => `${c.id}(${c.services.length}svcs $${c.totalSavings})`).join(', '));
        console.log('📊 [FE-BREAKDOWN] Summary:', data.data.summary);
        if (data.data.categories.length > 0 && !activeCategory) {
          setActiveCategory(data.data.categories[0].id);
        }
        // Pass savings + findings count to parent for header stats
        // Exclude Potential Benefits (policy recs with $0 savings)
        const actionableSavings = (data.data.categories || [])
          .filter((c: any) => c.id !== 'Potential Benefits')
          .reduce((sum: number, c: any) => sum + (c.totalSavings || 0), 0);
        const totalFindings = data.data.summary?.totalFindings || 0;
        onBreakdownLoad?.(actionableSavings, totalFindings);
      } else {
        console.warn('📊 [FE-BREAKDOWN] success:false —', data);
      }
    } catch (e) {
      console.error('❌ [FE-BREAKDOWN] fetch failed:', e);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchBreakdown(); }, [fetchBreakdown]);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/accounts`, { headers: getAuthHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        const awsAccounts = (data.accounts || [])
          .filter((a: any) => a.type === 'aws' && a.status === 'active')
          .map((a: any) => ({ accountId: a.accountId, name: a.name }));
        setConnectedAccounts(awsAccounts);
      } catch (e) {}
    };
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (!externalScanTrigger) return;
    startScan();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalScanTrigger]);

  const startScan = async () => {
    setIsScanning(true);
    setScanProgress('Starting AWS scan...');
    console.log('🔍 [FE-SCAN] Starting AWS scan...');
    
    let playInterval: ReturnType<typeof setInterval> | null = null;
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    
    try {
      const res = await fetch(`${API_BASE}/api/cost/scan/aws`, {
        method: 'POST', headers: getAuthHeaders()
      });
      const data = await res.json();
      console.log('🔍 [FE-SCAN] Start response:', data);
      if (!data.success) {
        console.warn('🔍 [FE-SCAN] Scan start failed:', data.error);
        setScanProgress(data.error || 'Scan failed');
        setIsScanning(false);
        return;
      }
      const scanId = data.scanId || data.data?.scanId;
      console.log('🔍 [FE-SCAN] scanId:', scanId);
      if (!scanId) {
        console.error('🔍 [FE-SCAN] No scanId in response — full response:', JSON.stringify(data));
        setIsScanning(false);
        setScanProgress('Scan failed: no scanId returned');
        return;
      }

      // Initialize play queue
      const playQueue = [
        'Starting AWS scan...',
        'Connecting to your AWS account & setting up scan...'
      ];
      let currentPlayIndex = 0;
      let backendFinished = false;
      let backendFailed = false;
      let finalError = '';

      setScanProgress(playQueue[0]);
      onScanProgress?.(playQueue[0]);

      // Set playback interval to cycle messages every 800ms
      playInterval = setInterval(() => {
        if (currentPlayIndex < playQueue.length - 1) {
          currentPlayIndex++;
          const msg = playQueue[currentPlayIndex];
          setScanProgress(msg);
          onScanProgress?.(msg);
        } else if (backendFinished) {
          // Playback has caught up and backend is done
          if (playInterval) clearInterval(playInterval);
          setIsScanning(false);
          setScanProgress('');
          fetchBreakdown();
          onScanComplete?.();
        } else if (backendFailed) {
          // Playback has caught up and backend failed
          if (playInterval) clearInterval(playInterval);
          setIsScanning(false);
          const errMsg = finalError || 'Scan failed';
          setScanProgress(errMsg);
          onScanProgress?.(errMsg);
        }
      }, 800);

      // Poll backend progress
      pollInterval = setInterval(async () => {
        try {
          const pRes = await fetch(`${API_BASE}/api/cost/scan/aws/${scanId}/progress`, { headers: getAuthHeaders() });
          if (!pRes.ok) {
            console.error('❌ [FE-SCAN] Progress endpoint returned status:', pRes.status);
            return;
          }
          const pData = await pRes.json();
          console.log('🔍 [FE-SCAN] Poll status:', pData.status, '| queue len:', pData.messageQueue?.length);
          
          if (pData.success || pData.status) {
            // Add all items from backend messageQueue into our playQueue if they aren't already there
            const newTasks = pData.messageQueue || [];
            if (pData.currentTask && !newTasks.includes(pData.currentTask)) {
              newTasks.push(pData.currentTask);
            }
            newTasks.forEach((msg: any) => {
              if (msg && typeof msg === 'string' && !playQueue.includes(msg)) {
                if (msg.includes('Identifying idle')) return; // skip identifying idle & wasteful message
                playQueue.push(msg);
              }
            });

            if (pData.status === 'completed') {
              if (pollInterval) clearInterval(pollInterval);
              backendFinished = true;
            } else if (pData.status === 'failed') {
              if (pollInterval) clearInterval(pollInterval);
              backendFailed = true;
              finalError = pData.error;
            }
          }
        } catch (pollErr) {
          console.error('❌ [FE-SCAN] Poll error:', pollErr);
          if (pollInterval) clearInterval(pollInterval);
          if (playInterval) clearInterval(playInterval);
          setIsScanning(false);
          setScanProgress('Scan progress check failed');
        }
      }, 5000);

      // Safety timeout after 15 minutes
      setTimeout(() => {
        if (pollInterval) clearInterval(pollInterval);
        if (playInterval) clearInterval(playInterval);
        setIsScanning(false);
      }, 15 * 60 * 1000);

    } catch (e: any) {
      console.error('❌ [FE-SCAN] fetch error:', e);
      setIsScanning(false);
      setScanProgress('Scan failed');
    }
  };

  const executeRemediation = async (findings: Finding[]) => {
    const ids = findings.map(f => f.resourceId);
    console.log('🗑️ [FE-REMEDIATE] Executing for ids:', ids);
    setRemediatingIds(prev => { const next = new Set(prev); ids.forEach(id => next.add(id)); return next; });
    try {
      const res = await fetch(`${API_BASE}/api/cost/waste/remediate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ findingIds: ids, dryRun: false })
      });
      console.log('🗑️ [FE-REMEDIATE] HTTP status:', res.status);
      const data = await res.json();
      console.log('🗑️ [FE-REMEDIATE] Response:', data);
      if (data.success) {
        const results = data.data.results || [];
        const successes = results.filter((r: any) => r.status === 'success').length;
        const failures = results.filter((r: any) => r.status === 'failed').length;
        console.log(`🗑️ [FE-REMEDIATE] Done — success:${successes} failed:${failures}`);
        setRemediationResults(results);
        setShowResultsModal(true);
        fetchBreakdown();
      } else {
        console.warn('🗑️ [FE-REMEDIATE] success:false —', data.error);
      }
    } catch (e) {
      console.error('❌ [FE-REMEDIATE] fetch error:', e);
    } finally {
      setRemediatingIds(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
    }
  };

  const executeSnooze = async (findings: Finding[], days: number, reason: string) => {
    console.log(`🔕 [FE-SNOOZE] Snoozing ${findings.length} findings for ${days} days. reason: "${reason}"`);
    try {
      const results = await Promise.all(findings.map(f =>
        fetch(`${API_BASE}/api/cost/recommendations/snooze`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ resourceId: f.resourceId, days, reason })
        }).then(r => r.json())
      ));
      console.log('🔕 [FE-SNOOZE] Results:', results);
      fetchBreakdown();
    } catch (e) {
      console.error('❌ [FE-SNOOZE] error:', e);
    }
  };

  const currentCategory = breakdown?.categories.find(c => c.id === activeCategory);

  // Only show real AWS regions that have findings — exclude 'global', 'multi-region' etc.
  const AWS_REGION_PATTERN = /^(us|eu|ap|ca|sa|af|me|il)-[a-z]+-\d+$/;
  const allRegions = React.useMemo(() => {
    if (!breakdown) return ['All Regions'];
    const regions = new Set<string>();
    breakdown.categories.forEach(cat => cat.services.forEach(svc => svc.findings.forEach(f => {
      if (f.region && AWS_REGION_PATTERN.test(f.region)) regions.add(f.region);
    })));
    return ['All Regions', ...Array.from(regions).sort()];
  }, [breakdown]);

  // Build accountId → name map from connected accounts
  const accountNameMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    connectedAccounts.forEach(a => { if (a.accountId) map[a.accountId] = a.name || a.accountId; });
    return map;
  }, [connectedAccounts]);

  // Reverse map: name → accountId (handles cases where findings store name instead of ID)
  const accountIdByName = React.useMemo(() => {
    const map: Record<string, string> = {};
    connectedAccounts.forEach(a => { if (a.name && a.accountId) map[a.name] = a.accountId; });
    return map;
  }, [connectedAccounts]);

  const exportToExcel = useCallback(async () => {
    if (!breakdown) return;

    const ExcelJS = await import('exceljs');
    const wb = new ExcelJS.Workbook();

    const HEADER_BG  = '1D4ED8'; // blue-700 — matches the UI table header
    const HEADER_FG  = 'FFFFFF';

    const headerStyle = (ws: any, headers: string[], colWidths: number[]) => {
      const row = ws.getRow(1);
      headers.forEach((h, i) => {
        const cell = row.getCell(i + 1);
        cell.value = h;
        cell.font   = { bold: true, color: { argb: `FF${HEADER_FG}` }, size: 10 };
        cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${HEADER_BG}` } };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: false };
      });
      row.height = 22;
      colWidths.forEach((w, i) => { ws.getColumn(i + 1).width = w; });
    };

    const getFilteredRows = (cat: Category) => {
      const rows: { serviceName: string; finding: Finding }[] = [];
      cat.services.forEach(svc => {
        svc.findings.forEach(f => {
          if (!showSnoozed && f.snoozed) return;
          if (regionFilter !== 'All Regions' && f.region !== regionFilter) return;
          if (accountFilter !== 'All Accounts' && f.accountId !== accountFilter && f.accountId !== 'N/A') return;
          rows.push({ serviceName: svc.serviceName, finding: f });
        });
      });
      rows.sort((a, b) => b.finding.potentialSavings - a.finding.potentialSavings);
      return rows;
    };

    const STD_HEADERS = ['S', 'Account ID', 'Resource', 'Region', 'Service Name', 'Potential Savings($)', 'Recommendation Age', 'Description', 'Action'];
    const STD_WIDTHS  = [5, 18, 36, 14, 26, 20, 22, 55, 42];
    const PB_WIDTHS   = [5, 18, 36, 14, 26, 20, 16, 22, 55, 42];
    const SHEET_ORDER = ['Cleaner', 'OverProvisioned', 'Modernization', 'Potential Benefits'];

    const applyHeaderCell = (cell: any, value: string) => {
      cell.value     = value;
      cell.font      = { bold: true, color: { argb: `FF${HEADER_FG}` }, size: 10 };
      cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${HEADER_BG}` } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: false };
    };

    const buildStdHeader = (ws: any, headers: string[], widths: number[]) => {
      const row = ws.getRow(1);
      row.height = 22;
      headers.forEach((h, i) => applyHeaderCell(row.getCell(i + 1), h));
      widths.forEach((w, i) => { ws.getColumn(i + 1).width = w; });
    };

    // Potential Benefits: 2-row header — merge FIRST then set value to avoid duplicate "SS"
    const buildPBHeader = (ws: any) => {
      PB_WIDTHS.forEach((w, i) => { ws.getColumn(i + 1).width = w; });
      ws.getRow(1).height = 18;
      ws.getRow(2).height = 18;

      // Cols A-E: merge rows 1+2 first, then set value on master cell
      ['S', 'Account ID', 'Resource', 'Region', 'Service Name'].forEach((h, i) => {
        ws.mergeCells(1, i + 1, 2, i + 1);
        applyHeaderCell(ws.getCell(1, i + 1), h);
      });

      // Col F-G row 1: merge then set
      ws.mergeCells(1, 6, 1, 7);
      applyHeaderCell(ws.getCell(1, 6), 'Unit Name & Value');

      // Col F-G row 2: sub-headers
      applyHeaderCell(ws.getCell(2, 6), 'Unit Name');
      applyHeaderCell(ws.getCell(2, 7), 'Unit Value');

      // Cols H-J: merge rows 1+2 then set
      ['Recommendation Age', 'Description', 'Action'].forEach((h, i) => {
        const col = 8 + i;
        ws.mergeCells(1, col, 2, col);
        applyHeaderCell(ws.getCell(1, col), h);
      });
    };

    const sortedCats = [...breakdown.categories].sort((a, b) => {
      const ai = SHEET_ORDER.indexOf(a.id);
      const bi = SHEET_ORDER.indexOf(b.id);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

    for (const cat of sortedCats) {
      const isPB = cat.id === 'Potential Benefits';
      const rows = getFilteredRows(cat);
      const ws   = wb.addWorksheet(cat.label.slice(0, 31));

      if (isPB) {
        buildPBHeader(ws);
      } else {
        buildStdHeader(ws, STD_HEADERS, STD_WIDTHS);
      }

      // Wrap: Resource(3), Description, Action — STD: 8,9  PB: 9,10
      const wrapCols = isPB ? new Set([3, 9, 10]) : new Set([3, 8, 9]);

      rows.forEach((r, i) => {
        const f    = r.finding as any;
        // If finding stores name as accountId, look up the real numeric ID
        const displayAccountId = accountIdByName[f.accountId] || f.accountId;
        // Safe savings parse — handles number or string like "39.6-"
        const rawSavings = parseFloat(String(f.potentialSavings ?? 0));
        const savings = isNaN(rawSavings) || rawSavings <= 0 ? 0 : parseFloat(rawSavings.toFixed(2));

        const base = [
          i + 1,
          displayAccountId,
          f.resourceName || f.resourceId,
          AWS_REGION_NAMES[f.region] || f.region,
          r.serviceName,
        ];
        const values = isPB
          ? [...base, f.unitName || '', f.unitValue || '', formatAge(f.ageInDays), f.description, f.action]
          : [...base, savings, formatAge(f.ageInDays), f.description, f.action];

        const dataRow = ws.addRow(values);
        dataRow.font  = { size: 10 };

        dataRow.eachCell({ includeEmpty: true }, (cell: any, colNum: number) => {
          if (wrapCols.has(colNum)) {
            cell.alignment = { vertical: 'top', horizontal: 'center', wrapText: true };
          } else {
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: false };
          }
        });
      });
    }

    const buffer = await wb.xlsx.writeBuffer();
    const blob   = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url    = URL.createObjectURL(blob);
    const a      = document.createElement('a');
    const date   = new Date().toISOString().slice(0, 10);
    a.href       = url;
    a.download   = `finops-recommendations-${date}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }, [breakdown, regionFilter, accountFilter, showSnoozed, accountNameMap, accountIdByName]);

  // Collect all accounts for filter — prefer connected account list, fallback to findings
  const allAccounts = React.useMemo(() => {
    if (connectedAccounts.length > 0) {
      return [
        { value: 'All Accounts', label: 'All Accounts' },
        ...connectedAccounts.map(a => ({ value: a.accountId, label: a.name || a.accountId }))
      ];
    }
    if (!breakdown) return [];
    const accounts = new Set<string>();
    breakdown.categories.forEach(cat => cat.services.forEach(svc => svc.findings.forEach(f => { if (f.accountId && f.accountId !== 'N/A') accounts.add(f.accountId); })));
    return [{ value: 'All Accounts', label: 'All Accounts' }, ...Array.from(accounts).sort().map(a => ({ value: a, label: a }))];
  }, [breakdown, connectedAccounts]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white border border-gray-200 rounded animate-pulse h-14" />
        ))}
      </div>
    );
  }

  if (!breakdown || breakdown.summary.requiresScan) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-10 text-center">
        <FontAwesomeIcon icon={faAws} className="text-orange-500 text-4xl mb-3" />
        <div className="text-sm font-semibold text-gray-900 mb-2">No scan data available</div>
        <div className="text-xs text-gray-500 mb-5">Run an AWS scan to discover savings opportunities across your infrastructure.</div>
        <button onClick={startScan} disabled={isScanning}
          className="px-5 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm font-medium">
          {isScanning ? <><FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />Scanning...</> : <><FontAwesomeIcon icon={faServer} className="mr-2" />Scan AWS</>}
        </button>
        {isScanning && <div className="mt-3 text-xs text-gray-500">{scanProgress}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-3 font-mono">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900">Recommendation Breakdown</div>
          {breakdown.summary.lastScanAt && (
            <div className="text-xs text-gray-400 mt-0.5 flex items-center space-x-1">
              <FontAwesomeIcon icon={faInfoCircle} className="text-xs" />
              <span>Last refresh: {new Date(breakdown.summary.lastScanAt).toLocaleString()}</span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {breakdown.summary.snoozedCount > 0 && (
            <span className="text-xs text-yellow-600 bg-yellow-50 border border-yellow-200 px-2 py-1 rounded">
              <FontAwesomeIcon icon={faBell} className="mr-1" />{breakdown.summary.snoozedCount} snoozed
            </span>
          )}
          {allAccounts.length > 1 && (
            <select
              value={accountFilter}
              onChange={e => setAccountFilter(e.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none"
            >
              {allAccounts.map(a => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          )}
          <select
            value={regionFilter}
            onChange={e => setRegionFilter(e.target.value)}
            className="text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none"
          >
            {allRegions.map(r => (
              <option key={r} value={r}>
                {r === 'All Regions' ? 'All Regions' : `${AWS_REGION_NAMES[r] ? `${AWS_REGION_NAMES[r]} (${r})` : r}`}
              </option>
            ))}
          </select>
          <button onClick={startScan} disabled={isScanning}
            className="px-3 py-1.5 bg-orange-600 text-white rounded text-xs hover:bg-orange-700 disabled:opacity-50 flex items-center space-x-1">
            {isScanning ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : <FontAwesomeIcon icon={faSync} />}
            <span>{isScanning ? 'Scanning...' : 'Re-scan'}</span>
          </button>
          <button
            onClick={exportToExcel}
            title="Download Excel"
            className="px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 flex items-center space-x-1"
          >
            <FontAwesomeIcon icon={faFileExcel} />
            <span>Excel</span>
          </button>
        </div>
      </div>

      {isScanning && (
        <div className="bg-blue-50 border border-blue-200 rounded px-4 py-2 text-xs text-blue-700 flex items-center space-x-2">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
          <span>{scanProgress}</span>
        </div>
      )}

      {/* Category Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {breakdown.categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => { setActiveCategory(cat.id); setExpandedService(null); }}
              className={`px-5 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${activeCategory === cat.id
                ? 'border-blue-500 text-blue-700 bg-blue-50'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
            >
              {cat.label}
              {/* Potential Benefits are policy recommendations — no direct savings to show */}
              {cat.id !== 'Potential Benefits' && cat.totalSavings > 0 && (
                <div className="text-xs font-normal text-gray-500 mt-0.5">
                  ({formatSavings(cat.totalSavings)})
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Services list */}
        {currentCategory && (
          currentCategory.services.length === 0 ? (
            <div className="p-8 text-center text-xs text-gray-400">No findings in this category</div>
          ) : (
            currentCategory.services.map(service => (
              <ServiceRow
                key={service.serviceId}
                service={service}
                isExpanded={expandedService === `${activeCategory}:${service.serviceId}`}
                onToggle={() => setExpandedService(
                  expandedService === `${activeCategory}:${service.serviceId}`
                    ? null
                    : `${activeCategory}:${service.serviceId}`
                )}
                regionFilter={regionFilter}
                accountFilter={accountFilter}
                showSnoozed={showSnoozed}
                onRemediateOne={f => setConfirmFindings([f])}
                onRemediateBulk={findings => setConfirmFindings(findings)}
                onSnoozeOne={f => setSnoozeFindings([f])}
                onSnoozeBulk={findings => setSnoozeFindings(findings)}
                remediatingIds={remediatingIds}
                accountNameMap={accountNameMap}
              />
            ))
          )
        )}
      </div>

      {/* Confirm Remediation Modal */}
      {confirmFindings && (
        <ConfirmModal
          findings={confirmFindings}
          isBulk={confirmFindings.length > 1}
          onConfirm={() => {
            const f = confirmFindings;
            setConfirmFindings(null);
            executeRemediation(f);
          }}
          onCancel={() => setConfirmFindings(null)}
        />
      )}

      {/* Snooze Modal */}
      {snoozeFindings && (
        <SnoozeModal
          findings={snoozeFindings}
          onConfirm={(days, reason) => {
            const f = snoozeFindings;
            setSnoozeFindings(null);
            executeSnooze(f, days, reason);
          }}
          onCancel={() => setSnoozeFindings(null)}
        />
      )}

      {/* Remediation Results Modal */}
      {showResultsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg mx-4">
            <div className="bg-green-600 rounded-t-lg px-5 py-3 flex items-center justify-between">
              <div className="flex items-center space-x-2 text-white">
                <FontAwesomeIcon icon={faCheck} />
                <span className="font-semibold text-sm">Remediation Complete</span>
              </div>
              <button onClick={() => setShowResultsModal(false)} className="text-white hover:text-green-200">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="p-5 max-h-96 overflow-y-auto">
              {remediationResults.map((r, i) => (
                <div key={i} className={`flex items-center justify-between py-2 border-b border-gray-100 last:border-0`}>
                  <div>
                    <div className="text-xs font-mono text-gray-800">{r.resourceName || r.resourceId}</div>
                    <div className="text-xs text-gray-500">{r.resourceType}</div>
                    {r.error && <div className="text-xs text-red-600 mt-0.5">{r.error}</div>}
                  </div>
                  <div className="flex items-center space-x-2">
                    {r.status === 'success' && <span className="text-green-600 text-xs flex items-center space-x-1"><FontAwesomeIcon icon={faCheck} /><span>Done</span></span>}
                    {r.status === 'failed' && <span className="text-red-600 text-xs flex items-center space-x-1"><FontAwesomeIcon icon={faTimes} /><span>Failed</span></span>}
                    {r.potentialSavings > 0 && <span className="text-xs font-semibold text-green-700">${r.potentialSavings.toFixed(2)}/mo</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-gray-100">
              <button onClick={() => setShowResultsModal(false)}
                className="w-full py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
